<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    nextTick,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
  } from 'vue';
  import { usePreferredReducedMotion } from '@vueuse/core';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import { useDraftActions } from '@/composition/menu-import/use-draft-actions';
  import type { ILiveLineAction } from '@/domain/menu-import/live-rows';
  import { queueEntryKey, queueView } from '@/domain/menu-import/review-queue';
  import type {
    IAnswerQuestionDto,
    IMenuDraft,
    IMenuImportPage,
    TDraftOp,
  } from '@/interfaces/IMenuImport';
  import V2MenuQuestionCard from './V2MenuQuestionCard.vue';
  import V2MenuReviewQueueItem from './V2MenuReviewQueueItem.vue';

  /**
   * «Revisar uno por uno» (plan §4.5.4), in place of the list: one thing at
   * a time, open blocking questions first (as their question card), then red
   * dishes, then amber ones, in menu order, recomputed after each action.
   * The card on screen stays put while the owner works on it (a delta that
   * reorders the queue does not swap it), and an answer moves on only once
   * it is saved. «Revisar {index} de {total}» counts what the owner already
   * went through in this pass; the end says «¡Listo! Revisó todo.».
   */
  const props = defineProps<{
    draft: IMenuDraft;
    pages: IMenuImportPage[];
    currency: string;
    country: string;
    /** Saves an answer; true once saved (a refused answer keeps its card). */
    answerQuestion: (
      questionId: string,
      payload: IAnswerQuestionDto
    ) => Promise<boolean>;
  }>();
  const emit = defineEmits<{
    (e: 'ops', ops: TDraftOp[]): void;
    (e: 'confirm', itemId: string): void;
    /** «Volver a la lista». */
    (e: 'done'): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const reducedMotion = usePreferredReducedMotion();
  const actions = useDraftActions({ country: () => props.country });

  const root = ref<HTMLElement | null>(null);
  const heading = ref<HTMLElement | null>(null);
  /** Entries answered, confirmed or skipped in this pass. */
  const acted = ref<ReadonlySet<string>>(new Set());
  /** Dishes corrected with «Guardar cambio» (reviewed once they leave the queue). */
  const saved = ref<ReadonlySet<string>>(new Set());
  /** The card on screen (kept current while it is still in the queue). */
  const pinnedKey = ref<string | null>(null);
  /** The question whose answer is being saved. */
  const answering = ref<string | null>(null);
  let alive = true;

  const view = computed(() =>
    queueView(props.draft, acted.value, saved.value, pinnedKey.value)
  );
  const current = computed(() => view.value.current);
  const currentKey = computed(() =>
    current.value ? queueEntryKey(current.value) : null
  );
  const question = computed(() => {
    const entry = current.value;
    return entry?.kind === 'question'
      ? props.draft.questions[entry.id] ?? null
      : null;
  });
  const item = computed(() => {
    const entry = current.value;
    return entry?.kind === 'item' ? props.draft.items[entry.id] ?? null : null;
  });
  const progress = computed(() =>
    tr('menuImport.queue.progress', {
      index: view.value.index,
      total: view.value.total,
    })
  );
  const fill = computed(() => {
    const { index, total } = view.value;
    return total > 0 ? `${Math.round(((index - 1) / total) * 100)}%` : '0%';
  });

  function blurInside() {
    const active = document.activeElement;
    if (active instanceof HTMLElement && root.value?.contains(active))
      active.blur();
  }

  /** The new card's heading takes the focus; the card comes into view when it starts above the screen. */
  async function focusHeading() {
    await nextTick();
    if (!alive) return;
    heading.value?.focus({ preventScroll: true });
    const top = root.value?.getBoundingClientRect().top ?? 0;
    if (top < 0)
      root.value?.scrollIntoView({
        block: 'start',
        behavior: reducedMotion.value === 'reduce' ? 'auto' : 'smooth',
      });
  }

  function markActed(key: string) {
    acted.value = new Set([...acted.value, key]);
  }

  /** On to the next entry: this one counts as reviewed in this pass. */
  function next() {
    const key = currentKey.value;
    if (key === null) return;
    blurInside();
    markActed(key);
  }

  // The card on screen stays pinned; a new card (or the end) takes the focus:
  // the previous card's controls are gone.
  watch(
    currentKey,
    (key, previous) => {
      pinnedKey.value = key;
      if (previous !== undefined && key !== previous) void focusHeading();
    },
    { immediate: true }
  );

  /** Moves on only once the answer is saved; a refused one keeps its card. */
  async function onAnswer(payload: IAnswerQuestionDto) {
    const open = question.value;
    const key = currentKey.value;
    if (!open || key === null || answering.value !== null) return;
    answering.value = open.id;
    const answered = await props.answerQuestion(open.id, payload);
    if (!alive) return;
    answering.value = null;
    // The answered question already left the queue: its key only counts it as reviewed.
    if (answered) markActed(key);
  }

  function onSave(ops: TDraftOp[]) {
    const key = currentKey.value;
    if (key === null) return;
    saved.value = new Set([...saved.value, key]);
    emit('ops', ops);
  }

  /** A live-line decision (a link, a new dish, today's price): then on to the next, like «Está bien». */
  function onLive(action: ILiveLineAction) {
    const dish = item.value;
    if (!dish) return;
    if (action.kind === 'link' && action.liveItemId !== undefined)
      void actions.linkLive(dish.id, action.liveItemId);
    else if (action.kind === 'new') void actions.linkLive(dish.id, null);
    else if (action.kind === 'keepPrice') void actions.keepLivePrice(dish.id);
    else if (action.kind === 'keepSizes') void actions.keepLiveSizes(dish.id);
    next();
  }

  function onConfirm(ops: TDraftOp[]) {
    const dish = item.value;
    if (!dish) return;
    if (ops.length > 0)
      emit('ops', [...ops, { op: 'confirmReview', itemIds: [dish.id] }]);
    else emit('confirm', dish.id);
    next();
  }

  onMounted(() => {
    void focusHeading();
  });

  onBeforeUnmount(() => {
    alive = false;
  });
</script>

<template>
  <section ref="root" class="v2-menu-review-queue">
    <template v-if="current">
      <div class="queue-bar">
        <PosUiButton
          color="neutral"
          variant="ghost"
          icon="arrow-left"
          @click="emit('done')"
          >{{ $t('menuImport.queue.back') }}</PosUiButton
        >
      </div>
      <header class="queue-head">
        <h2 ref="heading" class="queue-title" tabindex="-1">
          {{ progress }}
        </h2>
        <div class="queue-track" aria-hidden="true">
          <span class="queue-fill" :style="{ width: fill }" />
        </div>
      </header>
      <div v-if="question" class="queue-question">
        <V2MenuQuestionCard
          :key="currentKey"
          :question="question"
          :draft="draft"
          :currency="currency"
          :country="country"
          :busy="answering !== null"
          @answer="onAnswer"
        />
        <button
          type="button"
          class="queue-skip"
          :disabled="answering !== null"
          @click="next"
        >
          {{ $t('menuImport.queue.skip') }}
        </button>
      </div>
      <V2MenuReviewQueueItem
        v-else-if="item"
        :key="currentKey"
        :item="item"
        :pages="pages"
        :currency="currency"
        :country="country"
        :live-others="draft.live ? draft.live.others : null"
        @save="onSave"
        @confirm="onConfirm"
        @skip="next"
        @live="onLive"
      />
    </template>
    <div v-else class="queue-done">
      <PosUiIcon name="check-circle" class="done-icon" :size="40" />
      <h2 ref="heading" class="queue-title" tabindex="-1">
        {{ $t('menuImport.queue.done') }}
      </h2>
      <PosUiButton color="neutral" icon="arrow-left" @click="emit('done')">{{
        $t('menuImport.queue.back')
      }}</PosUiButton>
    </div>
  </section>
</template>

<style scoped lang="scss">
  .v2-menu-review-queue {
    @apply tw-flex tw-flex-col tw-gap-3;

    .queue-bar {
      @apply -tw-ml-2 tw-flex tw-min-h-11 tw-items-center;
    }
    .queue-head {
      @apply tw-flex tw-flex-col tw-gap-2;
    }
    .queue-title {
      @apply tw-m-0 tw-text-base tw-font-semibold tw-text-neutral-900 md:tw-text-lg;
      &:focus {
        @apply tw-outline-none;
      }
    }
    .queue-track {
      @apply tw-h-1 tw-w-full tw-overflow-hidden tw-rounded-full tw-bg-neutral-200;
    }
    .queue-fill {
      @apply tw-block tw-h-full tw-rounded-full tw-bg-emerald-500 tw-transition-all tw-duration-300;
    }
    .queue-question {
      @apply tw-flex tw-flex-col tw-gap-2;
    }
    .queue-skip {
      @apply tw-min-h-11 tw-cursor-pointer tw-self-end tw-border-0 tw-bg-transparent tw-px-2 tw-text-sm tw-font-medium tw-text-neutral-600 tw-underline tw-underline-offset-4 hover:tw-text-neutral-900;
      &:focus-visible {
        @apply tw-rounded tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
      &:disabled {
        @apply tw-cursor-not-allowed tw-text-neutral-400 tw-no-underline hover:tw-text-neutral-400;
      }
    }
    .queue-done {
      @apply tw-flex tw-flex-col tw-items-center tw-gap-3 tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-px-4 tw-py-8 tw-text-center;
      .done-icon {
        @apply tw-text-emerald-600;
      }
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .v2-menu-review-queue .queue-fill {
      transition: none;
    }
  }
</style>
